import { formatDistanceToNowStrict, parseISO } from 'date-fns';

export const EMAIL_INGESTION_FORWARD_ADDRESS = 'docs@adultingapp.uk';

export type EmailIngestionRecord = {
  id?: string;
  user_id?: string | null;
  resolved_user_id?: string | null;
  created_at?: string | null;
  received_at?: string | null;
  processed_at?: string | null;
  status?: string | null;
  storage_status?: string | null;
  subject?: string | null;
  from_email?: string | null;
  sender_email?: string | null;
  recipient_email?: string | null;
  matched_account_email?: string | null;
  matched_user_email?: string | null;
  attachment_count?: number | null;
  action_count?: number | null;
  action_manifest?: unknown;
  document_count?: number | null;
  file_count?: number | null;
  document_ids?: unknown;
  notes?: string | null;
  queued_reason?: string | null;
  error_message?: string | null;
  provider?: string | null;
  [key: string]: unknown;
};

export type EmailIngestionHistoryItem = {
  id: string;
  title: string;
  meta: string;
  body: string;
  tone: 'neutral' | 'success' | 'warning' | 'destructive';
};

export type EmailIngestionSummary = {
  headline: string;
  body: string;
  accountEmailLabel: string;
  latestStatusLabel: string;
  history: EmailIngestionHistoryItem[];
};

function pickText(row: EmailIngestionRecord, keys: string[]) {
  for (const key of keys) {
    const value = row[key];
    if (typeof value === 'string' && value.trim()) {
      return value.trim();
    }
  }
  return '';
}

function pickNumber(row: EmailIngestionRecord, keys: string[]) {
  for (const key of keys) {
    const value = row[key];
    if (typeof value === 'number' && Number.isFinite(value)) {
      return value;
    }
  }
  return 0;
}

function formatWhen(value: string | null | undefined) {
  if (!value) {
    return 'just now';
  }

  try {
    return formatDistanceToNowStrict(parseISO(value), { addSuffix: true });
  } catch {
    return new Date(value).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  }
}

function statusLabel(value: string | null | undefined) {
  if (!value) {
    return 'received';
  }

  if (value === 'queued_external_provider') {
    return 'queued for external provider';
  }

  if (value === 'stored') {
    return 'stored in Documents';
  }

  return value.replace(/[_-]+/g, ' ').trim();
}

function toneForRow(row: EmailIngestionRecord): EmailIngestionHistoryItem['tone'] {
  const status = `${row.storage_status || row.status || ''}`.toLowerCase();
  const errorMessage = `${row.error_message || ''}`.trim();

  if (errorMessage || status.includes('error') || status.includes('failed')) {
    return 'destructive';
  }

  if (status.includes('processed') || status.includes('matched') || status.includes('saved') || status.includes('ready')) {
    return 'success';
  }

  if (status.includes('pending') || status.includes('queued') || status.includes('waiting')) {
    return 'warning';
  }

  return 'neutral';
}

function countStoredDocuments(row: EmailIngestionRecord) {
  const counted = pickNumber(row, ['document_count', 'file_count', 'stored_document_count']);
  if (counted > 0) {
    return counted;
  }

  if (Array.isArray(row.document_ids)) {
    return row.document_ids.length;
  }

  return 0;
}

function getActionManifestItems(row: EmailIngestionRecord) {
  return Array.isArray(row.action_manifest) ? row.action_manifest.filter((item) => item && typeof item === 'object') as Array<Record<string, unknown>> : [];
}

function countTrackerUpdates(row: EmailIngestionRecord) {
  const counted = pickNumber(row, ['action_count']);
  if (counted > 0) {
    return counted;
  }

  return getActionManifestItems(row).length;
}

function listTrackerUpdateLabels(row: EmailIngestionRecord) {
  return getActionManifestItems(row)
    .map((item) => (typeof item.label === 'string' ? item.label.trim() : ''))
    .filter(Boolean)
    .slice(0, 2);
}

export async function loadEmailIngestionHistory(
  client: any,
  userId: string,
  limit = 4,
) {
  if (!client) {
    return {
      rows: [] as EmailIngestionRecord[],
      errorMessage: 'Could not read email ingestion history from this build yet.',
    };
  }

  try {
    const result = await client
      .from('email_ingestions')
      .select('*')
      .eq('resolved_user_id', userId)
      .order('received_at', { ascending: false })
      .limit(limit);

    if (result.error) {
      return {
        rows: [] as EmailIngestionRecord[],
        errorMessage: result.error.message || 'Could not load email ingestion history yet.',
      };
    }

    return {
      rows: (result.data || []) as EmailIngestionRecord[],
      errorMessage: null,
    };
  } catch (error) {
    return {
      rows: [] as EmailIngestionRecord[],
      errorMessage: error instanceof Error ? error.message : 'Could not load email ingestion history yet.',
    };
  }
}

export function buildEmailIngestionSummary(rows: EmailIngestionRecord[], accountEmail: string | null | undefined): EmailIngestionSummary {
  const accountEmailLabel = accountEmail?.trim() || 'your Adulting account email';
  const latest = rows[0] || null;
  const latestStatusLabel = latest ? statusLabel(latest.storage_status || latest.status || latest.provider || null) : 'waiting for the first forwarded email';

  const headline = rows.length
    ? 'Forwarded email ingestion is ready'
    : `Forward mail to ${EMAIL_INGESTION_FORWARD_ADDRESS}`;

  const body = rows.length
    ? `Adulting matches forwarded mail to ${accountEmailLabel} before filing attachments and applying supported updates.`
    : `Forward messages and attachments from the mailbox that belongs to ${accountEmailLabel}. Adulting will match them to this account before filing attachments and applying supported updates.`;

  return {
    headline,
    body,
    accountEmailLabel,
    latestStatusLabel,
    history: rows.map((row, index) => {
      const title = pickText(row, ['subject', 'from_email', 'sender_email', 'recipient_email']) || `Forwarded email ${index + 1}`;
      const source = pickText(row, ['from_email', 'sender_email']);
      const recipient = pickText(row, ['recipient_email', 'matched_account_email', 'matched_user_email']);
      const attachmentCount = pickNumber(row, ['attachment_count', 'file_count']);
      const storedDocumentCount = countStoredDocuments(row);
      const trackerUpdateCount = countTrackerUpdates(row);
      const trackerUpdateLabels = listTrackerUpdateLabels(row);
      const queuedReason = pickText(row, ['queued_reason', 'notes']);
      const errorMessage = pickText(row, ['error_message']);
      const matchTarget = recipient || accountEmailLabel;
      const bodyParts = [];

      if (source) {
        bodyParts.push(`From ${source}`);
      }

      if (matchTarget) {
        bodyParts.push(`Matched to ${matchTarget}`);
      }

      if (attachmentCount > 0) {
        bodyParts.push(`${attachmentCount} attachment${attachmentCount === 1 ? '' : 's'}`);
      }

      if (storedDocumentCount > 0) {
        bodyParts.push(`${storedDocumentCount} stored document${storedDocumentCount === 1 ? '' : 's'}`);
      }

      if (trackerUpdateCount > 0) {
        bodyParts.push(
          trackerUpdateLabels.length
            ? trackerUpdateLabels.join(' • ')
            : `${trackerUpdateCount} tracker update${trackerUpdateCount === 1 ? '' : 's'}`
        );
      }

      if (queuedReason) {
        bodyParts.push(queuedReason);
      }

      if (errorMessage) {
        bodyParts.push(errorMessage);
      }

      return {
        id: `${row.id || row.created_at || index}`,
        title,
        meta: [
          formatWhen(row.created_at || row.received_at || row.processed_at || null),
          statusLabel(row.storage_status || row.status || null),
        ]
          .filter(Boolean)
          .join(' • '),
        body: bodyParts.join(' • ') || `Matched to ${accountEmailLabel}`,
        tone: toneForRow(row),
      };
    }),
  };
}
